import { useMemo } from "react";
import { WeekDay } from "client/Hooks";
import { TimeZone } from "client/DateAndTime/timezones";
import {
	addDays,
	calculateDayDifference,
	millisecondsInAMinute,
	parseDtoDate
} from "client/DateAndTime/dateTimeUtils";
import {
	findLastIndex,
	partition,
	sortBy
} from "client_legacy/ndaw/arrayHelpers";

export type WeekViewItemKey<TItem> = StringKeyOf<TItem> | ((item: TItem, index: number) => number | string);

export type WeekViewLayout<TItem> = {
	hourRange: WeekViewHourRange | undefined;
	allDayItems: readonly WeekViewAllDayItem<TItem>[];
	days: readonly WeekViewDayLayout<TItem>[];
};

export type WeekViewHourRange = {
	startHour: number;
	endHour: number;
};

export type WeekViewAllDayItem<TItem> = {
	item: TItem;
	key: number | string;
	row: number;
	firstDayIndex: number;
	lastDayIndex: number;
};

export type WeekViewDayLayout<TItem> = {
	day: WeekDay;
	timedItems: readonly WeekViewTimedItem<TItem>[];
};

export type WeekViewTimedItem<TItem> = {
	item: TItem;
	key: number | string;
	startTimeInMinutes: number;
	endTimeInMinutes: number;
	column: number;
	columnSpan: number;
	columnCount: number;
};

export const MINUTES_PER_HOUR = 60;

export function useWeekViewLayout<TItem>(
	items: readonly TItem[],
	days: readonly WeekDay[],
	itemKey: WeekViewItemKey<TItem>,
	startDateTimeRetriever: (item: TItem) => string | Date,
	endDateTimeRetriever: (item: TItem) => string | Date,
	timezone: TimeZone
): WeekViewLayout<TItem> {
	return useMemo(
		(): WeekViewLayout<TItem> => {
			const parsedItems: readonly ParsedItem<TItem>[] = items.map(
				(x, i): ParsedItem<TItem> => {
					const start: Date = parseDtoDate(startDateTimeRetriever(x));
					const end: Date = parseDtoDate(endDateTimeRetriever(x));

					return {
						item: x,
						key: resolveItemKey(x, i, itemKey),
						start: start.getTime(),
						end: end.getTime(),
						isAllDay: calculateDayDifference(end, start, timezone, true) >= 1
					};
				}
			);

			const [allDayItems, timedItems] = partition(
				sortBy(parsedItems, (x) => x.start),
				(x) => x.isAllDay
			);

			const dayRanges: readonly TimeRange[] = days.map(
				(x): TimeRange => ({
					start: x.date.getTime(),
					end: addDays(x.date, 1, timezone).getTime()
				})
			);

			const timedItemsPerDay: readonly (readonly DayItem<TItem>[])[] = dayRanges.map(
				(x): readonly DayItem<TItem>[] => getTimedItemsOfTheDay(timedItems, x)
			);

			return {
				hourRange: getHourRange(timedItemsPerDay.flat()),
				allDayItems: packAllDayItems(allDayItems, dayRanges),
				days: days.map(
					(x, i): WeekViewDayLayout<TItem> => ({
						day: x,
						timedItems: groupOverlappingItems(timedItemsPerDay[i]).flatMap(packGroup)
					})
				)
			};
		},
		[
			items,
			days,
			itemKey,
			startDateTimeRetriever,
			endDateTimeRetriever,
			timezone
		]
	);
}

type TimeRange = {
	start: number;
	end: number;
};

type ParsedItem<TItem> = TimeRange & {
	item: TItem;
	key: number | string;
	isAllDay: boolean;
};

type DayItem<TItem> = {
	item: TItem;
	key: number | string;
	startTimeInMinutes: number;
	endTimeInMinutes: number;
};

function resolveItemKey<TItem>(
	item: TItem,
	index: number,
	itemKey: WeekViewItemKey<TItem>
): number | string {
	if (typeof itemKey === "function") {
		return itemKey(item, index);
	}

	const key: unknown = item[itemKey];

	if (typeof key === "string" || typeof key === "number") {
		return key;
	}

	console.error("Key must be either string or number. Falling back to index");

	return index;
}

function getTimedItemsOfTheDay<TItem>(
	items: readonly ParsedItem<TItem>[],
	day: TimeRange
): readonly DayItem<TItem>[] {
	return items
		.filter(
			(x) => x.start < day.end
				&& (x.end > day.start || x.start >= day.start)
		)
		.map((x): DayItem<TItem> => ({
			item: x.item,
			key: x.key,
			startTimeInMinutes: (Math.max(x.start, day.start) - day.start) / millisecondsInAMinute,
			endTimeInMinutes: (Math.min(x.end, day.end) - day.start) / millisecondsInAMinute
		}));
}

function getHourRange<TItem>(
	items: readonly DayItem<TItem>[]
): WeekViewHourRange | undefined {
	if (items.length === 0) {
		return undefined;
	}

	const startTimeInMinutes: number = Math.min(...items.map((x) => x.startTimeInMinutes));
	const endTimeInMinutes: number = Math.max(...items.map((x) => x.endTimeInMinutes));

	return {
		startHour: Math.floor(startTimeInMinutes / MINUTES_PER_HOUR),
		endHour: Math.min(
			Math.floor(endTimeInMinutes / MINUTES_PER_HOUR),
			LAST_HOUR_OF_THE_DAY
		)
	};
}

const LAST_HOUR_OF_THE_DAY = 23;

function packAllDayItems<TItem>(
	items: readonly ParsedItem<TItem>[],
	days: readonly TimeRange[]
): readonly WeekViewAllDayItem<TItem>[] {
	const nextFreeDayIndexPerRow: number[] = [];
	const result: WeekViewAllDayItem<TItem>[] = [];

	for (const item of items) {
		const isOnTheDay = (day: TimeRange): boolean => item.start < day.end
			&& item.end > day.start;

		const firstDayIndex: number = days.findIndex(isOnTheDay);

		if (firstDayIndex === -1) {
			continue;
		}

		const lastDayIndex: number = findLastIndex(days, isOnTheDay);
		const row: number = getFirstFreeSlot(nextFreeDayIndexPerRow, firstDayIndex);

		nextFreeDayIndexPerRow[row] = lastDayIndex + 1;

		result.push({
			item: item.item,
			key: item.key,
			row,
			firstDayIndex,
			lastDayIndex
		});
	}

	return result;
}

function groupOverlappingItems<TItem>(
	items: readonly DayItem<TItem>[]
): readonly (readonly DayItem<TItem>[])[] {
	const groups: DayItem<TItem>[][] = [];
	let groupEndTimeInMinutes = 0;

	for (const item of items) {
		if (groups.length === 0 || item.startTimeInMinutes >= groupEndTimeInMinutes) {
			groups.push([]);
		}

		groups[groups.length - 1].push(item);
		groupEndTimeInMinutes = Math.max(groupEndTimeInMinutes, item.endTimeInMinutes);
	}

	return groups;
}

type ColumnItem<TItem> = DayItem<TItem> & {
	column: number;
};

function packGroup<TItem>(
	group: readonly DayItem<TItem>[]
): readonly WeekViewTimedItem<TItem>[] {
	const endTimeInMinutesPerColumn: number[] = [];
	const columnItems: ColumnItem<TItem>[] = [];

	for (const item of group) {
		const column: number = getFirstFreeSlot(endTimeInMinutesPerColumn, item.startTimeInMinutes);

		endTimeInMinutesPerColumn[column] = item.endTimeInMinutes;

		columnItems.push({
			...item,
			column
		});
	}

	const columnCount: number = endTimeInMinutesPerColumn.length;

	return columnItems.map((x): WeekViewTimedItem<TItem> => ({
		...x,
		columnSpan: getColumnSpan(x, columnItems, columnCount),
		columnCount
	}));
}

function getColumnSpan<TItem>(
	item: ColumnItem<TItem>,
	group: readonly ColumnItem<TItem>[],
	columnCount: number
): number {
	const nextOccupiedColumn: number = Math.min(
		columnCount,
		...group
			.filter(
				(x) => x.column > item.column
					&& doItemsOverlap(x, item)
			)
			.map((x) => x.column)
	);

	return nextOccupiedColumn - item.column;
}

function doItemsOverlap<TItem>(first: DayItem<TItem>, second: DayItem<TItem>): boolean {
	return first.startTimeInMinutes < second.endTimeInMinutes
		&& first.endTimeInMinutes > second.startTimeInMinutes;
}

function getFirstFreeSlot(endPerSlot: readonly number[], start: number): number {
	const freeSlot: number = endPerSlot.findIndex((x) => x <= start);

	return freeSlot !== -1
		? freeSlot
		: endPerSlot.length;
}
